/**
 * ユーザーラベルの2段目に出す「表示項目の値」と「アイコン」を求める。
 *
 * 値の書式はユーザーリストの詳細カードと同じにする必要があるため、
 * カードが使っているのと同じ処理（`composables/user/additionalItem/`）をそのまま呼ぶ。
 * 二重実装にすると単位や括弧の付け方がカードとずれるため。
 *
 * 呼び出し側の想定は `custom-layers/SpeechBalloonLabelLayer.js` の2行目。
 */

import { effectScope, ref } from 'vue'
import { ITEM_TYPE } from '@/composables/member/UseUserStatusAdditionalItems'
import {
  useUserStatusAdditionalItemAccuracy,
  useUserStatusAdditionalItemActivity,
  useUserStatusAdditionalItemBattery,
  useUserStatusAdditionalItemIndoor,
  useUserStatusAdditionalItemPlace,
  useUserStatusAdditionalItemPositioning,
  useUserStatusAdditionalItemRegistration,
  useUserStatusAdditionalItemSensorData,
  useUserStatusAdditionalItemStatus,
  useUserStatusAdditionalItemUserAttribute,
} from '@/composables/user/additionalItem'

/**
 * 表示項目とアイコンの対応
 *
 * 詳細カードが使っているアイコンと同じものを指す（`assets/ifield/ic_*.svg`）。
 * ユーザー属性はカードにもアイコンが無いので持たない
 * @type {Object.<String, String>}
 */
export const ITEM_ICON = {
  [ITEM_TYPE.STATUS]: 'status',
  [ITEM_TYPE.INDOOR]: 'building',
  [ITEM_TYPE.PLACE]: 'place',
  [ITEM_TYPE.BATTERY]: 'battery',
  [ITEM_TYPE.ACTIVITY]: 'operating-status',
  [ITEM_TYPE.SENSOR_DATA]: 'sensor',
  [ITEM_TYPE.POSITIONING]: 'positioning-time',
  [ITEM_TYPE.ACCURACY]: 'positioning-accuracy',
  [ITEM_TYPE.ACCURACY_RATING]: 'positioning-accuracy',
}

/**
 * 値が無いときに出す文字
 *
 * カードの各 composable が値無しに出すものと同じにする
 * @type {String}
 */
const EMPTY_VALUE_TEXT = '-'

/**
 * 表示項目の種類を返す
 * 表示項目のキーは `"item_type:id"` の形式を取ることがある
 * @param {Object} item 表示項目の値
 * @returns {String} 種類
 */
export const getItemType = (item) => `${item?.item_type ?? ''}`.split(':')[0]

/**
 * 表示項目のアイコン名を返す
 * @param {Object} item 表示項目の値
 * @returns {String|null} アイコン名
 */
export const getItemIcon = (item) => {
  const type = getItemType(item)
  // プレイスはチェックイン中かどうかでアイコンが変わる（カードと同じ）
  if (type === ITEM_TYPE.PLACE) return item?.checkin_at ? 'checkin' : 'place'
  return ITEM_ICON[type] || null
}

/**
 * 表示項目の色の丸（ステイタスの色）を返す
 *
 * 仕様（cluster.png）: ステイタスは値の後ろに色の丸を出す。
 * リストの詳細カードと同じで、ステイタス以外は色の丸を持たない
 * @param {Object} item 表示項目の値
 * @returns {String|null} 色
 */
export const getItemDotColor = (item) =>
  getItemType(item) === ITEM_TYPE.STATUS ? item?.color || null : null

/**
 * リストのcomposableを呼んで結果の文字列を取り出す
 *
 * 2つの理由でこの包みが要る。
 * 1. **引数は必ず ref で渡す。** リスト側のcomposableには `unref` を使わず
 *    `changeAt.value` のように直接読んでいる箇所があり（稼働状態）、
 *    生の値を渡すと `undefined` になって表示が壊れる
 * 2. **使い終わったら破棄する。** composable は `computed` を作る。
 *    コンポーネントの外で作った `computed` は持ち主がいないため後片付けされず、
 *    参照した先（i18nなど）に紐づいたまま残る。ラベルは地物ごとに毎秒評価されるので、
 *    放っておくと際限なく積み上がる
 * @param {Function} fn composable を呼んで文字列を返す関数
 * @returns {String} 表示する文字列
 */
const runOnce = (fn) => {
  const scope = effectScope(true)
  try {
    return scope.run(fn) ?? ''
  } finally {
    scope.stop()
  }
}

/**
 * 表示項目の値を1行の文字列にする
 * @param {Object} item 表示項目の値
 * @param {Date} baseDate 相対時間を計算する際の基準日時
 * @returns {String} 表示する文字列
 */
export const getItemText = (item, baseDate) =>
  runOnce(() => buildItemText(item, ref(baseDate)))

/**
 * 表示項目の値を1行の文字列にする（composableの呼び出し本体）
 * @param {Object} item 表示項目の値
 * @param {Object} baseDate 基準日時(ref)
 * @returns {String} 表示する文字列
 */
const buildItemText = (item, baseDate) => {
  if (!item) return ''

  switch (getItemType(item)) {
    case ITEM_TYPE.STATUS:
      return useUserStatusAdditionalItemStatus({
        statusName: ref(item.status_name),
      }).text.value

    case ITEM_TYPE.INDOOR:
      return useUserStatusAdditionalItemIndoor({
        buildingName: ref(item.building_name),
        buildingShortName: ref(item.building_short_name),
        levelName: ref(item.level_name),
        levelShortName: ref(item.level_short_name),
      }).text.value

    case ITEM_TYPE.PLACE:
      return useUserStatusAdditionalItemPlace({
        placeName: ref(item.place_name),
      }).text.value

    case ITEM_TYPE.BATTERY:
      return useUserStatusAdditionalItemBattery({ value: ref(item.value) }).text
        .value

    case ITEM_TYPE.ACTIVITY:
      return useUserStatusAdditionalItemActivity({
        value: ref(item.value),
        changeAt: ref(item.change_at),
        baseDate,
      }).text.value

    case ITEM_TYPE.SENSOR_DATA:
      return useUserStatusAdditionalItemSensorData({
        sensorItemName: ref(item.sensor_item_name),
        value: ref(item.value),
        unit: ref(item.unit),
        valueType: ref(item.value_type),
      }).text.value

    case ITEM_TYPE.ATTRIBUTE:
      return useUserStatusAdditionalItemUserAttribute({
        attributeType: ref(item.attribute_type),
        attributeName: ref(item.attribute_name),
        value: ref(item.value),
      }).text.value

    case ITEM_TYPE.POSITIONING:
      return useUserStatusAdditionalItemPositioning({
        at: ref(item.at),
        baseDate,
      }).text.value

    case ITEM_TYPE.ACCURACY:
      return useUserStatusAdditionalItemAccuracy({
        accuracy: ref(item.accuracy),
      }).accuracyText.value

    case ITEM_TYPE.ACCURACY_RATING:
      return useUserStatusAdditionalItemAccuracy({
        accuracy: ref(item.accuracy),
        rating: ref(item.rating),
        positioningStatus: ref(item.positioning_status),
      }).ratingText.value

    case ITEM_TYPE.REGISTRATION:
      return useUserStatusAdditionalItemRegistration({
        at: ref(item.at),
        baseDate,
      }).text.value

    // 紐付けデバイス名・紐付けセンサー名は、カードでは値のうしろに項目名が並ぶが、
    // 項目名が常に同じ文字列でラベルの幅を食うだけなので、ラベルは値だけを出す
    // （プレイス固有の項目で項目名を出さないのと同じ考え方。IFIELD_V4-7276）。
    // どちらも紐付けが無いと null、レスポンスが空文字のこともあるため falsy で判定する
    case ITEM_TYPE.DEVICE:
      return item.device_name || EMPTY_VALUE_TEXT

    case ITEM_TYPE.SENSOR_NAMES:
      return item.names || EMPTY_VALUE_TEXT

    default:
      return ''
  }
}
